import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../lib/cn';

const ACCENTS = {
  good: 'border-l-[3px] border-l-good',
  danger: 'border-l-[3px] border-l-danger',
  neutral: 'border-l-[3px] border-l-line-strong',
};

export function Card({
  title,
  description,
  actions,
  className,
  bodyClassName,
  lift,
  accent,
  style,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Raise slightly on hover (for clickable cards). */
  lift?: boolean;
  /** Colored left edge. */
  accent?: keyof typeof ACCENTS;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <section
      style={style}
      className={cn('rise rounded-card border border-line bg-surface shadow-card', lift && 'lift', accent && ACCENTS[accent], className)}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-2 px-5 pt-4 pb-1">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-semibold text-ink">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn('px-5 py-4', bodyClassName)}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
